/**
 * Decide how a selection gets uploaded (AP-865).
 *
 * Replaces `prepareUploadPayload`, which zipped every multi-file selection in
 * the browser. That zip keyed entries on the basename, so two files with the
 * same name in different subfolders overwrote each other and the archive came
 * out short — a nested study folder is exactly that shape.
 */

/** A pre-made archive the user picked: still one object, still one signed URL. */
export interface ArchiveSelection {
  kind: 'archive';
  file: File;
}

/** Loose files or a folder: one signed URL each, uploaded in parallel. */
export interface BatchSelection {
  kind: 'batch';
  files: File[];
}

export type Selection = ArchiveSelection | BatchSelection;

/**
 * The path the server orders and names objects by. Folder picks populate
 * `webkitRelativePath`; a plain multi-file pick leaves it empty, and then the
 * bare name is all the ordering information that exists.
 */
export function relativePathOf(file: File): string {
  return file.webkitRelativePath || file.name;
}

export function classifySelection(files: File[]): Selection {
  if (files.length === 0) {
    throw new Error('No files to upload');
  }
  if (files.length === 1 && files[0].name.toLowerCase().endsWith('.zip')) {
    return { kind: 'archive', file: files[0] };
  }
  return { kind: 'batch', files };
}

/** The top folder of a folder pick, or null for a loose file. */
export function topFolderOf(file: File): string | null {
  const path = file.webkitRelativePath;
  const slash = path ? path.indexOf('/') : -1;
  return slash > 0 ? path.slice(0, slash) : null;
}

/** How a selection is named to the user: its folder, its one file, or a count. */
export function selectionLabel(files: File[]): string {
  const folders = new Set(files.map(topFolderOf));
  if (folders.size === 1) {
    const [folder] = folders;
    if (folder) return folder;
  }
  return files.length === 1 ? files[0].name : `${files.length} files`;
}
